import type { Workbook, WorkbookProfile, AnswerKeyEntry } from '@ellie/shared';
import type { SourceLanguageCode, TargetLanguageCode } from '@ellie/shared';
import './workbook-preview.css';

import { CoverPage } from './CoverPage';
import { WelcomePage } from './WelcomePage';
import { TableOfContents } from './TableOfContents';
import { PageBreak } from './PageBreak';
import { BlockRenderer } from './blocks/BlockRenderer';
import { VerbGlossary } from './VerbGlossary';
import { CulturalCorner } from './CulturalCorner';
import { AnswerKey } from './AnswerKey';
import { BackCover } from './BackCover';

interface WorkbookPreviewProps {
  workbook: Workbook;
  profile: WorkbookProfile;
}

/**
 * Root component for the visual workbook preview.
 * Renders the full workbook: cover, welcome page, TOC, all exercise blocks,
 * verb glossary, cultural notes, and back cover.
 */
export function WorkbookPreview({ workbook, profile }: WorkbookPreviewProps) {
  const nativeLanguage = profile.native_language as SourceLanguageCode;
  const targetLanguage = profile.target_language as TargetLanguageCode;

  const hasWelcomePage = !!workbook.welcome_page;

  // Compute approximate page numbers for each block
  // Cover = page 1, Welcome = page 2 (if present), TOC = page 2 or 3,
  // first block starts at page 3 or 4
  const firstBlockPage = hasWelcomePage ? 4 : 3;
  const pageNumbers: number[] = [];
  let currentPage = firstBlockPage;
  for (const block of workbook.blocks) {
    pageNumbers.push(currentPage);
    currentPage += Math.ceil(block.estimatedPages);
  }

  const totalPages = workbook.page_count ?? currentPage + 4; // +4 for glossary, cultural, back cover

  return (
    <div className="wb-preview">
      {/* Cover */}
      <CoverPage
        coverData={workbook.cover_data}
        nativeLanguage={nativeLanguage}
        targetLanguage={targetLanguage}
      />
      <PageBreak />

      {/* Welcome Page (between cover and TOC) */}
      {workbook.welcome_page && (
        <>
          <WelcomePage welcomePage={workbook.welcome_page} />
          <PageBreak />
        </>
      )}

      {/* Table of Contents */}
      <TableOfContents
        blocks={workbook.blocks}
        totalPages={totalPages}
        firstBlockPage={firstBlockPage}
      />
      <PageBreak />

      {/* Exercise blocks */}
      {workbook.blocks.map((block, index) => (
        <div key={block.id}>
          <BlockRenderer
            block={block}
            index={index}
            nativeLanguage={nativeLanguage}
            targetLanguage={targetLanguage}
            pageNumber={pageNumbers[index]}
          />
          <PageBreak />
        </div>
      ))}

      {/* Verb Glossary */}
      {workbook.verb_glossary && workbook.verb_glossary.length > 0 && (
        <>
          <VerbGlossary entries={workbook.verb_glossary} />
          <PageBreak />
        </>
      )}

      {/* Cultural Corner */}
      {workbook.cultural_notes && workbook.cultural_notes.length > 0 && (
        <>
          <CulturalCorner notes={workbook.cultural_notes} />
          <PageBreak />
        </>
      )}

      {/* Answer Key */}
      {workbook.answer_key && workbook.answer_key.length > 0 && (
        <>
          <AnswerKey entries={workbook.answer_key as AnswerKeyEntry[]} />
          <PageBreak />
        </>
      )}

      {/* Back Cover */}
      <BackCover coverData={workbook.cover_data} targetLanguage={targetLanguage} />
    </div>
  );
}
